import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import {
  GlobeAltIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  HomeIcon,
  TagIcon,
  LinkIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import {
  getGoogleIndexingStatus,
  getUrlInspectionStatus,
  getBulkIndexStats,
  bulkIndexSite,
  submitCrawlSeeds,
  submitUrl,
  getIndexingLog,
  getInspectionSummary,
  getInspectionResults,
  getUrlsNeedingInspection,
  inspectSingleUrl,
  batchInspectUrls,
  getCannibalizationGroups,
  getClusterSaturation,
  getSubmissionHealthSummary,
  getSubmissionHealth,
  releaseHeldUrl,
  holdUrl,
  archivePost,
  runVerdictAnalysis,
  getManualIndexingQueue,
  markManuallySubmitted,
  type IndexingLogEntry,
  type UrlInspectionResult,
  type BulkIndexResponse,
  type CannibalizationGroup,
  type ClusterSaturation,
  type SubmissionHealthEntry,
} from '@/services/seoIndexingApi';

type ActiveTab = 'indexing-status' | 'submission-log' | 'content-health';
type VerdictFilter = '' | 'PASS' | 'NOT_INDEXED' | 'NOT_CHECKED';
type TypeFilter = '' | 'blog' | 'static' | 'category';

export default function SEOIndexing() {
  const queryClient = useQueryClient();
  const [manualUrl, setManualUrl] = useState('');
  const [submissionResults, setSubmissionResults] = useState<BulkIndexResponse | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('indexing-status');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('');
  const [inspectionPage, setInspectionPage] = useState(0);

  // Fetch Google Indexing API status
  const { data: googleStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['google-indexing-status'],
    queryFn: getGoogleIndexingStatus,
    staleTime: 60 * 1000,
  });

  // Fetch URL Inspection API status
  const { data: inspectionApiStatus } = useQuery({
    queryKey: ['url-inspection-status'],
    queryFn: getUrlInspectionStatus,
    staleTime: 60 * 1000,
  });

  // Fetch inspection summary (indexed/not indexed counts)
  const { data: inspectionSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['inspection-summary'],
    queryFn: getInspectionSummary,
    staleTime: 60 * 1000,
  });

  // Fetch site stats
  const { data: siteStats, isLoading: statsLoading } = useQuery({
    queryKey: ['bulk-index-stats'],
    queryFn: getBulkIndexStats,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch inspection results (for Indexing Status tab)
  const { data: inspectionData, isLoading: inspectionLoading } = useQuery({
    queryKey: ['inspection-results', verdictFilter, typeFilter, inspectionPage],
    queryFn: () =>
      getInspectionResults({
        verdict: verdictFilter || undefined,
        url_type: typeFilter || undefined,
        limit: 50,
        offset: inspectionPage * 50,
      }),
    staleTime: 30 * 1000,
    enabled: activeTab === 'indexing-status',
  });

  // Fetch recent indexing log (for Submission Log tab)
  const { data: logData, isLoading: logLoading } = useQuery({
    queryKey: ['indexing-log'],
    queryFn: () => getIndexingLog(50),
    staleTime: 30 * 1000,
    enabled: activeTab === 'submission-log',
  });

  // Bulk index mutation
  const bulkIndexMutation = useMutation({
    mutationFn: (type: 'all' | 'static' | 'blogs' | 'categories') => bulkIndexSite(type),
    onSuccess: (data) => {
      setSubmissionResults(data);
      queryClient.invalidateQueries({ queryKey: ['google-indexing-status'] });
      queryClient.invalidateQueries({ queryKey: ['indexing-log'] });
    },
  });

  // Crawl seed submission mutation (bulk discovery workflow)
  const crawlSeedMutation = useMutation({
    mutationFn: () => submitCrawlSeeds(),
    onSuccess: (data) => {
      setSubmissionResults(data);
      queryClient.invalidateQueries({ queryKey: ['google-indexing-status'] });
      queryClient.invalidateQueries({ queryKey: ['indexing-log'] });
    },
  });

  // Manual URL submission mutation
  const submitUrlMutation = useMutation({
    mutationFn: (url: string) => submitUrl(url, 'URL_UPDATED'),
    onSuccess: () => {
      setManualUrl('');
      queryClient.invalidateQueries({ queryKey: ['google-indexing-status'] });
      queryClient.invalidateQueries({ queryKey: ['indexing-log'] });
    },
  });

  // Single URL inspection mutation
  const inspectMutation = useMutation({
    mutationFn: (url: string) => inspectSingleUrl(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-summary'] });
      queryClient.invalidateQueries({ queryKey: ['inspection-results'] });
      queryClient.invalidateQueries({ queryKey: ['url-inspection-status'] });
    },
  });

  // Batch inspection mutation (Check All)
  const batchInspectMutation = useMutation({
    mutationFn: async () => {
      const urls = await getUrlsNeedingInspection(100);
      if (urls.length === 0) return null;
      return batchInspectUrls(
        urls.map((u) => u.url),
        100
      );
    },
    onSettled: () => {
      // Always refresh data — partial results may be saved even on timeout/error
      queryClient.invalidateQueries({ queryKey: ['inspection-summary'] });
      queryClient.invalidateQueries({ queryKey: ['inspection-results'] });
      queryClient.invalidateQueries({ queryKey: ['url-inspection-status'] });
    },
  });

  // Content Health queries
  const [healthFilter, setHealthFilter] = useState<string>('');
  const [healthPage, setHealthPage] = useState(0);

  const { data: cannibalizationGroups, isLoading: groupsLoading } = useQuery({
    queryKey: ['cannibalization-groups'],
    queryFn: () => getCannibalizationGroups(),
    staleTime: 5 * 60 * 1000,
    enabled: activeTab === 'content-health',
  });

  const { data: clusterSaturation, isLoading: saturationLoading } = useQuery({
    queryKey: ['cluster-saturation'],
    queryFn: getClusterSaturation,
    staleTime: 5 * 60 * 1000,
    enabled: activeTab === 'content-health',
  });

  const { data: submissionHealthSummary } = useQuery({
    queryKey: ['submission-health-summary'],
    queryFn: getSubmissionHealthSummary,
    staleTime: 60 * 1000,
    enabled: activeTab === 'content-health',
  });

  const { data: submissionHealthData, isLoading: healthLoading } = useQuery({
    queryKey: ['submission-health', healthFilter, healthPage],
    queryFn: () => getSubmissionHealth({
      status: healthFilter || undefined,
      limit: 50,
      offset: healthPage * 50,
    }),
    staleTime: 30 * 1000,
    enabled: activeTab === 'content-health',
  });

  const releaseHoldMutation = useMutation({
    mutationFn: (url: string) => releaseHeldUrl(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submission-health'] });
      queryClient.invalidateQueries({ queryKey: ['submission-health-summary'] });
    },
  });

  const holdUrlMutation = useMutation({
    mutationFn: ({ url, reason }: { url: string; reason: string }) => holdUrl(url, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submission-health'] });
      queryClient.invalidateQueries({ queryKey: ['submission-health-summary'] });
    },
  });

  const archivePostMutation = useMutation({
    mutationFn: (postId: string) => archivePost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cannibalization-groups'] });
      queryClient.invalidateQueries({ queryKey: ['cluster-saturation'] });
    },
  });

  const verdictAnalysisMutation = useMutation({
    mutationFn: runVerdictAnalysis,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submission-health'] });
      queryClient.invalidateQueries({ queryKey: ['submission-health-summary'] });
    },
  });

  // Manual indexing queue
  const { data: manualQueue } = useQuery({
    queryKey: ['manual-indexing-queue'],
    queryFn: () => getManualIndexingQueue(),
    staleTime: 60 * 1000,
  });

  const markManualMutation = useMutation({
    mutationFn: (url: string) => markManuallySubmitted(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manual-indexing-queue'] });
    },
  });

  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const copyToClipboard = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualUrl.trim()) {
      submitUrlMutation.mutate(manualUrl.trim());
    }
  };

  const getVerdictIcon = (verdict: string | null | undefined) => {
    switch (verdict) {
      case 'PASS':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'PARTIAL':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'FAIL':
      case 'NEUTRAL':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getVerdictLabel = (verdict: string | null | undefined): string => {
    switch (verdict) {
      case 'PASS':
        return 'Indexed';
      case 'PARTIAL':
        return 'Partial';
      case 'FAIL':
        return 'Not Indexed';
      case 'NEUTRAL':
        return 'Excluded';
      default:
        return 'Not Checked';
    }
  };

  const getVerdictBadge = (verdict: string | null | undefined) => {
    const label = getVerdictLabel(verdict);
    const colors: Record<string, string> = {
      Indexed: 'bg-green-100 text-green-800',
      Partial: 'bg-yellow-100 text-yellow-800',
      'Not Indexed': 'bg-red-100 text-red-800',
      Excluded: 'bg-red-100 text-red-800',
      'Not Checked': 'bg-gray-100 text-gray-600',
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colors[label] || 'bg-gray-100 text-gray-600'}`}
      >
        {getVerdictIcon(verdict)}
        {label}
      </span>
    );
  };

  const getSubmissionStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'pending':
      case 'submitted':
        return <ArrowPathIcon className="w-5 h-5 text-yellow-500 animate-spin" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStrengthBadge = (score: number) => {
    let color = 'bg-red-100 text-red-800';
    if (score >= 70) color = 'bg-green-100 text-green-800';
    else if (score >= 40) color = 'bg-yellow-100 text-yellow-800';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
        {Math.round(score)}
      </span>
    );
  };

  const getSubmissionStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      indexed: { label: 'Indexed', color: 'bg-green-100 text-green-800' },
      never_submitted: { label: 'Never Sent', color: 'bg-gray-100 text-gray-600' },
      in_cooldown: { label: 'Cooldown', color: 'bg-blue-100 text-blue-800' },
      eligible: { label: 'Eligible', color: 'bg-purple-100 text-purple-800' },
      permanently_held: { label: 'Held', color: 'bg-red-100 text-red-800' },
      max_attempts: { label: 'Max Tries', color: 'bg-orange-100 text-orange-800' },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getSaturationColor = (level: string) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'moderate': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  const getServiceBadge = (service: string) => {
    if (service === 'google') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Google
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
        IndexNow
      </span>
    );
  };

  const getTypeBadge = (urlType: string) => {
    const colors: Record<string, string> = {
      blog: 'bg-purple-100 text-purple-800',
      static: 'bg-green-100 text-green-800',
      category: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-600',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[urlType] || colors.other}`}
      >
        {urlType.charAt(0).toUpperCase() + urlType.slice(1)}
      </span>
    );
  };

  const indexedCount = inspectionSummary?.indexed || 0;
  const notIndexedCount = inspectionSummary?.not_indexed || 0;
  const neverInspectedCount = inspectionSummary?.never_inspected || 0;
  const totalInspected = inspectionSummary?.total_inspected || 0;
  const totalUrls = totalInspected + neverInspectedCount;
  const indexedPercent = totalUrls > 0 ? Math.round((indexedCount / totalUrls) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SEO & Indexing</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor Google discovery/indexing. Bulk submissions are signals, not the same as Search Console "Request indexing".
          </p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Google Index Coverage Card (full width) */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Google Index Coverage</h2>
            <div className="flex items-center gap-3">
              {inspectionSummary?.last_inspection_run && (
                <span className="text-xs text-gray-500">
                  Last checked: {format(new Date(inspectionSummary.last_inspection_run), 'MMM d, h:mm a')}
                </span>
              )}
              <button
                onClick={() => batchInspectMutation.mutate()}
                disabled={batchInspectMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark disabled:opacity-50"
              >
                {batchInspectMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="w-4 h-4" />
                    Check All Status
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Batch inspection feedback */}
          {batchInspectMutation.isSuccess && batchInspectMutation.data && (
            <div className="mb-4 flex items-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg px-3 py-2">
              <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
              Inspected {batchInspectMutation.data.inspected} URLs
              {batchInspectMutation.data.failed > 0 && ` (${batchInspectMutation.data.failed} failed)`}
              {batchInspectMutation.data.timed_out && ' — stopped early due to time limit'}
              . Quota remaining: {batchInspectMutation.data.quota_remaining}
            </div>
          )}
          {batchInspectMutation.isError && (
            <div className="mb-4 flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 rounded-lg px-3 py-2">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
              Check timed out or encountered an error. Partial results may have been saved — data has been refreshed.
            </div>
          )}

          {summaryLoading ? (
            <div className="text-center text-gray-500 py-4">Loading...</div>
          ) : (
            <>
              {/* Coverage Numbers */}
              <div className="grid grid-cols-3 gap-6 mb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{indexedCount}</p>
                  <p className="text-sm text-gray-500">Indexed</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{notIndexedCount}</p>
                  <p className="text-sm text-gray-500">Not Indexed</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-400">{neverInspectedCount}</p>
                  <p className="text-sm text-gray-500">Not Checked</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                {totalUrls > 0 && (
                  <div className="flex h-full">
                    <div
                      className="bg-green-500 h-full"
                      style={{ width: `${indexedPercent}%` }}
                    />
                    <div
                      className="bg-red-400 h-full"
                      style={{ width: `${totalUrls > 0 ? Math.round((notIndexedCount / totalUrls) * 100) : 0}%` }}
                    />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500 text-center">
                {indexedPercent}% of {totalUrls} URLs are indexed by Google
              </p>

              {/* Coverage States Breakdown */}
              {inspectionSummary?.top_coverage_states && inspectionSummary.top_coverage_states.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-2">Coverage Breakdown</p>
                  <div className="space-y-1">
                    {inspectionSummary.top_coverage_states.map((state) => (
                      <div key={state.coverage_state} className="flex justify-between text-sm">
                        <span className="text-gray-600">{state.coverage_state}</span>
                        <span className="font-medium text-gray-900">{state.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Needs Manual Indexing Queue */}
      {manualQueue && manualQueue.length > 0 && (
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Needs Manual Indexing</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  These URLs aren't indexed yet. Copy and paste into{' '}
                  <a
                    href="https://search.google.com/search-console/inspect"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:text-brand-dark underline"
                  >
                    Google Search Console
                  </a>
                  {' '}and click "Request Indexing".
                </p>
              </div>
              <span className="text-sm text-gray-500">{manualQueue.length} URLs</span>
            </div>
            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {manualQueue.slice(0, 20).map((item) => (
                <div key={item.url} className="flex items-center justify-between py-2 gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {getTypeBadge(item.url_type)}
                      <span className="text-sm text-gray-900 truncate" title={item.url}>
                        {item.title || item.url.replace('https://linkedbysix.com', '')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-400">{item.coverage_state}</span>
                      {item.strength_score && (
                        <span className="text-xs text-gray-400">Strength: {item.strength_score}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => copyToClipboard(item.url)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                      title="Copy URL"
                    >
                      <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                      {copiedUrl === item.url ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={() => markManualMutation.mutate(item.url)}
                      disabled={markManualMutation.isPending}
                      className="px-2 py-1 text-xs font-medium text-brand bg-brand/10 rounded hover:bg-brand/20 disabled:opacity-50"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Google Indexing API Status */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${googleStatus?.configured ? 'bg-green-100' : 'bg-red-100'}`}>
                <GlobeAltIcon className={`w-6 h-6 ${googleStatus?.configured ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Indexing API</p>
                <p className={`text-lg font-semibold ${googleStatus?.configured ? 'text-green-600' : 'text-red-600'}`}>
                  {statusLoading ? '...' : googleStatus?.configured ? 'Active' : 'Off'}
                </p>
              </div>
            </div>
            {googleStatus?.configured && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Quota</span>
                  <span className="font-medium">
                    {googleStatus.quota_used_today} / {googleStatus.daily_quota}
                  </span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${(googleStatus.quota_used_today / googleStatus.daily_quota) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* URL Inspection API Status */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${inspectionApiStatus?.configured ? 'bg-green-100' : 'bg-red-100'}`}>
                <MagnifyingGlassIcon className={`w-6 h-6 ${inspectionApiStatus?.configured ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Inspection API</p>
                <p className={`text-lg font-semibold ${inspectionApiStatus?.configured ? 'text-green-600' : 'text-red-600'}`}>
                  {inspectionApiStatus?.configured ? 'Active' : 'Off'}
                </p>
              </div>
            </div>
            {inspectionApiStatus?.configured && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Quota</span>
                  <span className="font-medium">
                    {inspectionApiStatus.quota_used_today} / {inspectionApiStatus.daily_quota}
                  </span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${(inspectionApiStatus.quota_used_today / inspectionApiStatus.daily_quota) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* IndexNow Status */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-100">
                <GlobeAltIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">IndexNow</p>
                <p className="text-lg font-semibold text-green-600">Active</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Bing, Yandex, Seznam, Naver
              </p>
              <p className="mt-1 text-xs text-gray-400">No quota limit</p>
            </div>
          </CardBody>
        </Card>

        {/* Site Stats */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <DocumentTextIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Indexable URLs</p>
                <p className="text-lg font-semibold text-gray-900">
                  {statsLoading ? '...' : siteStats?.total_indexable || 0}
                </p>
              </div>
            </div>
            {siteStats && (
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Static</span>
                  <span className="font-medium">{siteStats.static_pages}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Blog</span>
                  <span className="font-medium">{siteStats.published_posts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Categories</span>
                  <span className="font-medium">{siteStats.active_categories}</span>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Bulk Indexing Actions */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Bulk Discovery + Submissions</h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <button
              onClick={() => crawlSeedMutation.mutate()}
              disabled={bulkIndexMutation.isPending || crawlSeedMutation.isPending}
              className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              title="Submit crawl seed URLs (sitemap + archive + categories). This helps discovery via crawling."
            >
              <ClipboardDocumentIcon className="w-8 h-8 text-slate-600" />
              <span className="text-sm font-medium text-gray-700">Crawl Seeds</span>
              <span className="text-xs text-gray-500">{(siteStats?.active_categories || 0) + 3} URLs</span>
            </button>

            <button
              onClick={() => bulkIndexMutation.mutate('all')}
              disabled={bulkIndexMutation.isPending || crawlSeedMutation.isPending}
              className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <GlobeAltIcon className="w-8 h-8 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Submit All</span>
              <span className="text-xs text-gray-500">{siteStats?.total_indexable || 0} URLs</span>
            </button>

            <button
              onClick={() => bulkIndexMutation.mutate('static')}
              disabled={bulkIndexMutation.isPending || crawlSeedMutation.isPending}
              className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <HomeIcon className="w-8 h-8 text-green-500" />
              <span className="text-sm font-medium text-gray-700">Submit Static</span>
              <span className="text-xs text-gray-500">{siteStats?.static_pages || 0} URLs</span>
            </button>

            <button
              onClick={() => bulkIndexMutation.mutate('blogs')}
              disabled={bulkIndexMutation.isPending || crawlSeedMutation.isPending}
              className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <DocumentTextIcon className="w-8 h-8 text-purple-500" />
              <span className="text-sm font-medium text-gray-700">Submit Posts</span>
              <span className="text-xs text-gray-500">{siteStats?.published_posts || 0} URLs</span>
            </button>

            <button
              onClick={() => bulkIndexMutation.mutate('categories')}
              disabled={bulkIndexMutation.isPending || crawlSeedMutation.isPending}
              className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <TagIcon className="w-8 h-8 text-orange-500" />
              <span className="text-sm font-medium text-gray-700">Submit Categories</span>
              <span className="text-xs text-gray-500">{siteStats?.active_categories || 0} URLs</span>
            </button>
          </div>

          {(bulkIndexMutation.isPending || crawlSeedMutation.isPending) && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              Submitting URL update signals...
            </div>
          )}

          {submissionResults && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Submission Results</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total URLs:</span>{' '}
                  <span className="font-medium">{submissionResults.total_urls}</span>
                </div>
                <div>
                  <span className="text-gray-500">Google:</span>{' '}
                  <span className={submissionResults.google_result?.success ? 'text-green-600' : 'text-red-600'}>
                    {submissionResults.google_result?.submitted || 0} submitted
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">IndexNow:</span>{' '}
                  <span className={submissionResults.indexnow_result?.success ? 'text-green-600' : 'text-red-600'}>
                    {submissionResults.indexnow_result?.submitted || 0} submitted
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Manual URL Submission */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Submit Single URL</h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleManualSubmit} className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  placeholder="https://linkedbysix.com/blog/your-post-slug"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  aria-label="URL to submit for indexing"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={!manualUrl.trim() || submitUrlMutation.isPending}
              className="px-6 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-dark disabled:opacity-50"
            >
              {submitUrlMutation.isPending ? 'Submitting...' : 'Submit URL'}
            </button>
          </form>

          {submitUrlMutation.isSuccess && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
              <CheckCircleIcon className="w-4 h-4" />
              URL submitted successfully to search engines
            </div>
          )}

          {submitUrlMutation.isError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
              <XCircleIcon className="w-4 h-4" />
              Failed to submit URL. Please try again.
            </div>
          )}
        </CardBody>
      </Card>

      {/* Tabbed Table: Indexing Status / Submission Log */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab('indexing-status')}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                activeTab === 'indexing-status'
                  ? 'bg-brand text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Indexing Status
            </button>
            <button
              onClick={() => setActiveTab('submission-log')}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                activeTab === 'submission-log'
                  ? 'bg-brand text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Submission Log
            </button>
            <button
              onClick={() => setActiveTab('content-health')}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                activeTab === 'content-health'
                  ? 'bg-brand text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Content Health
            </button>
          </div>
          {activeTab === 'indexing-status' && (
            <span className="text-sm text-gray-500">{inspectionData?.count || 0} inspected URLs</span>
          )}
          {activeTab === 'submission-log' && (
            <span className="text-sm text-gray-500">{logData?.count || 0} total entries</span>
          )}
          {activeTab === 'content-health' && (
            <span className="text-sm text-gray-500">
              {submissionHealthSummary?.total || 0} tracked URLs
            </span>
          )}
        </CardHeader>

        {/* Indexing Status Tab */}
        {activeTab === 'indexing-status' && (
          <>
            {/* Filters */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-4">
              <select
                value={verdictFilter}
                onChange={(e) => {
                  setVerdictFilter(e.target.value as VerdictFilter);
                  setInspectionPage(0);
                }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
              >
                <option value="">All Verdicts</option>
                <option value="PASS">Indexed</option>
                <option value="NOT_INDEXED">Not Indexed</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as TypeFilter);
                  setInspectionPage(0);
                }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
              >
                <option value="">All Types</option>
                <option value="blog">Blog</option>
                <option value="static">Static</option>
                <option value="category">Category</option>
              </select>
            </div>

            <CardBody padding="none">
              {inspectionLoading ? (
                <div className="p-6 text-center text-gray-500">Loading...</div>
              ) : !inspectionData || inspectionData.data.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No inspection data yet. Click "Check All Status" to inspect your URLs.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Google Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            URL
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Coverage State
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Crawled
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Checked
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {inspectionData.data.map((entry: UrlInspectionResult) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getVerdictBadge(entry.verdict)}
                            </td>
                            <td className="px-6 py-4">
                              <a
                                href={entry.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline truncate block max-w-xs"
                                title={entry.url}
                              >
                                {entry.url.replace('https://linkedbysix.com', '')}
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getTypeBadge(entry.url_type)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600">
                                {entry.coverage_state || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {entry.last_crawl_time
                                ? format(new Date(entry.last_crawl_time), 'MMM d, yyyy')
                                : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {format(new Date(entry.inspected_at), 'MMM d, h:mm a')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => inspectMutation.mutate(entry.url)}
                                disabled={inspectMutation.isPending}
                                className="text-sm text-brand hover:text-brand-dark font-medium disabled:opacity-50"
                                title="Re-check this URL"
                              >
                                Re-check
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {inspectionData.count > 50 && (
                    <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Showing {inspectionPage * 50 + 1}-
                        {Math.min((inspectionPage + 1) * 50, inspectionData.count)} of{' '}
                        {inspectionData.count}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setInspectionPage((p) => Math.max(0, p - 1))}
                          disabled={inspectionPage === 0}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setInspectionPage((p) => p + 1)}
                          disabled={(inspectionPage + 1) * 50 >= inspectionData.count}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </>
        )}

        {/* Content Health Tab */}
        {activeTab === 'content-health' && (
          <CardBody>
            {/* Submission Health Summary */}
            {submissionHealthSummary && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Submission Health</h3>
                  <button
                    onClick={() => verdictAnalysisMutation.mutate()}
                    disabled={verdictAnalysisMutation.isPending}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-brand border border-brand rounded-lg hover:bg-brand hover:text-white disabled:opacity-50"
                  >
                    {verdictAnalysisMutation.isPending ? (
                      <><ArrowPathIcon className="w-3 h-3 animate-spin" /> Analyzing...</>
                    ) : (
                      'Run Verdict Analysis'
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {[
                    { label: 'Indexed', value: submissionHealthSummary.indexed, color: 'text-green-600' },
                    { label: 'Eligible', value: submissionHealthSummary.eligible, color: 'text-purple-600' },
                    { label: 'In Cooldown', value: submissionHealthSummary.in_cooldown, color: 'text-blue-600' },
                    { label: 'Never Sent', value: submissionHealthSummary.never_submitted, color: 'text-gray-500' },
                    { label: 'Held', value: submissionHealthSummary.permanently_held, color: 'text-red-600' },
                    { label: 'Max Tries', value: submissionHealthSummary.max_attempts, color: 'text-orange-600' },
                  ].map((s) => (
                    <div key={s.label} className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-gray-500">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Fail Reason Breakdown */}
                {Object.keys(submissionHealthSummary.by_fail_reason).length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg">
                    <p className="text-xs font-medium text-red-700 mb-1">Fail Reasons</p>
                    <div className="space-y-1">
                      {Object.entries(submissionHealthSummary.by_fail_reason)
                        .sort(([, a], [, b]) => b - a)
                        .map(([reason, count]) => (
                          <div key={reason} className="flex justify-between text-xs">
                            <span className="text-red-600 truncate mr-2">{reason}</span>
                            <span className="font-medium text-red-800">{count}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cluster Saturation */}
            {!saturationLoading && clusterSaturation && clusterSaturation.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Cluster Saturation</h3>
                <div className="space-y-2">
                  {clusterSaturation
                    .sort((a: ClusterSaturation, b: ClusterSaturation) => b.post_count - a.post_count)
                    .slice(0, 10)
                    .map((cluster: ClusterSaturation) => (
                      <div key={cluster.cluster} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-40 truncate" title={cluster.cluster}>
                          {cluster.cluster || '(unclustered)'}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getSaturationColor(cluster.saturation_level)}`}
                            style={{ width: `${Math.min(100, (cluster.post_count / 200) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700 w-12 text-right">
                          {cluster.post_count}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          cluster.saturation_level === 'critical' ? 'bg-red-100 text-red-700' :
                          cluster.saturation_level === 'high' ? 'bg-orange-100 text-orange-700' :
                          cluster.saturation_level === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {cluster.saturation_level}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Cannibalization Groups */}
            {!groupsLoading && cannibalizationGroups && cannibalizationGroups.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Cannibalization Groups
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    ({new Set(cannibalizationGroups.map((g: CannibalizationGroup) => g.group_id)).size} groups,{' '}
                    {cannibalizationGroups.length} posts)
                  </span>
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {(() => {
                    // Group by group_id
                    const groups = new Map<number, CannibalizationGroup[]>();
                    for (const item of cannibalizationGroups) {
                      if (!groups.has(item.group_id)) groups.set(item.group_id, []);
                      groups.get(item.group_id)!.push(item);
                    }
                    return Array.from(groups.entries())
                      .sort(([, a], [, b]) => b.length - a.length)
                      .slice(0, 20)
                      .map(([groupId, items]) => {
                        const winner = items.reduce((a, b) => a.strength_score > b.strength_score ? a : b);
                        return (
                          <div key={groupId} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-500">
                                Group #{groupId} — {items[0]?.cluster || 'no cluster'} ({items.length} posts)
                              </span>
                            </div>
                            <div className="space-y-1">
                              {items
                                .sort((a, b) => b.strength_score - a.strength_score)
                                .map((item) => (
                                  <div
                                    key={item.post_id}
                                    className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${
                                      item.post_id === winner.post_id ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                                    }`}
                                  >
                                    {getStrengthBadge(item.strength_score)}
                                    <span className={`flex-1 truncate ${
                                      item.post_id === winner.post_id ? 'font-medium text-green-800' : 'text-gray-600'
                                    }`} title={item.title}>
                                      {item.title}
                                    </span>
                                    {item.google_verdict && getVerdictBadge(item.google_verdict)}
                                    {item.post_id !== winner.post_id && (
                                      <button
                                        onClick={() => archivePostMutation.mutate(item.post_id)}
                                        disabled={archivePostMutation.isPending}
                                        className="text-xs text-red-500 hover:text-red-700 font-medium shrink-0"
                                      >
                                        Archive
                                      </button>
                                    )}
                                    {item.post_id === winner.post_id && (
                                      <span className="text-xs text-green-600 font-medium shrink-0">Winner</span>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
            )}

            {/* Held URLs / Submission Tracker Table */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Submission Tracker</h3>
                <select
                  value={healthFilter}
                  onChange={(e) => { setHealthFilter(e.target.value); setHealthPage(0); }}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1"
                >
                  <option value="">All Status</option>
                  <option value="indexed">Indexed</option>
                  <option value="eligible">Eligible</option>
                  <option value="in_cooldown">In Cooldown</option>
                  <option value="permanently_held">Held</option>
                  <option value="max_attempts">Max Tries</option>
                  <option value="never_submitted">Never Sent</option>
                </select>
              </div>

              {healthLoading ? (
                <div className="text-center text-gray-500 py-4">Loading...</div>
              ) : !submissionHealthData || submissionHealthData.data.length === 0 ? (
                <div className="text-center text-gray-500 py-4 text-sm">No data in submission tracker</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">URL</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Sends</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Verdict</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Fail Reason</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Hold</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {submissionHealthData.data.map((entry: SubmissionHealthEntry) => (
                          <tr key={entry.url} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {getSubmissionStatusBadge(entry.submission_status)}
                            </td>
                            <td className="px-3 py-2">
                              <a
                                href={entry.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline truncate block max-w-[200px]"
                                title={entry.title || entry.url}
                              >
                                {entry.title || entry.url.replace('https://linkedbysix.com', '')}
                              </a>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {entry.total_submissions}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {entry.current_verdict ? getVerdictBadge(entry.current_verdict) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className="text-gray-500 truncate block max-w-[150px]" title={entry.fail_reason || ''}>
                                {entry.fail_reason || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                              {entry.permanently_held ? (
                                <span className="text-red-600 font-medium">{entry.hold_reason || 'Permanent'}</span>
                              ) : entry.hold_until ? (
                                format(new Date(entry.hold_until), 'MMM d')
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {entry.permanently_held ? (
                                <button
                                  onClick={() => releaseHoldMutation.mutate(entry.url)}
                                  disabled={releaseHoldMutation.isPending}
                                  className="text-brand hover:text-brand-dark font-medium disabled:opacity-50"
                                >
                                  Release
                                </button>
                              ) : !entry.permanently_held && entry.submission_status !== 'indexed' ? (
                                <button
                                  onClick={() => holdUrlMutation.mutate({ url: entry.url, reason: 'manual_hold' })}
                                  disabled={holdUrlMutation.isPending}
                                  className="text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                                >
                                  Hold
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {submissionHealthData.count > 50 && (
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Showing {healthPage * 50 + 1}-{Math.min((healthPage + 1) * 50, submissionHealthData.count)} of {submissionHealthData.count}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setHealthPage((p) => Math.max(0, p - 1))}
                          disabled={healthPage === 0}
                          className="px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setHealthPage((p) => p + 1)}
                          disabled={(healthPage + 1) * 50 >= submissionHealthData.count}
                          className="px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardBody>
        )}

        {/* Submission Log Tab */}
        {activeTab === 'submission-log' && (
          <CardBody padding="none">
            {logLoading ? (
              <div className="p-6 text-center text-gray-500">Loading...</div>
            ) : logData?.data.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No indexing submissions yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Google Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logData?.data.map((entry: IndexingLogEntry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getSubmissionStatusIcon(entry.status)}
                        </td>
                        <td className="px-6 py-4">
                          <a
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline truncate block max-w-xs"
                            title={entry.url}
                          >
                            {entry.url.replace('https://linkedbysix.com', '')}
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getServiceBadge(entry.service)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`text-xs font-medium ${entry.action === 'URL_UPDATED' ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {entry.action === 'URL_UPDATED' ? 'Update' : 'Delete'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {entry.google_verdict ? (
                            getVerdictBadge(entry.google_verdict)
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(entry.submitted_at), 'MMM d, h:mm a')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        )}
      </Card>
    </div>
  );
}
